import { Fragment, useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import type { SupportMessage } from "@/types/api";
import { MessageBubble } from "./MessageBubble";

// A gap of 4+ hours between messages triggers a session break divider.
const SESSION_BREAK_MS = 4 * 60 * 60 * 1000;

type ListItem =
  | { kind: "message"; message: SupportMessage }
  | { kind: "break"; date: Date };

function buildItems(messages: SupportMessage[]): ListItem[] {
  const items: ListItem[] = [];
  for (let i = 0; i < messages.length; i++) {
    if (i > 0) {
      const prev = new Date(messages[i - 1].created_at).getTime();
      const curr = new Date(messages[i].created_at).getTime();
      if (curr - prev > SESSION_BREAK_MS) {
        items.push({ kind: "break", date: new Date(messages[i].created_at) });
      }
    }
    items.push({ kind: "message", message: messages[i] });
  }
  return items;
}

function formatBreakDate(date: Date): string {
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) return "Today";
  if (isYesterday) return "Yesterday";
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

interface MessageListProps {
  messages: SupportMessage[];
  loading?: boolean;
  casePlaintext?: boolean;
  /** Enabled only by the native drawer after its current activation gate. */
  nativeAttribution?: boolean;
  /** Patient-owned canonical titles supplied by the native requests feed. */
  requestTitles?: ReadonlyMap<string, string>;
  /** Opens the request a titled reply belongs to (native drawer only). */
  onOpenRequest?: (id: string) => void;
  renderEvidence?: (message: SupportMessage) => ReactNode;
  /** Replace the default bubble for a message (return null to keep it). */
  renderMessage?: (message: SupportMessage) => ReactNode | null;
  /** False until the support thread has loaded. Request cards come from a separate feed and can
   *  arrive first; the first scroll to the newest message waits for the thread itself. */
  ready?: boolean;
  /** Quiet status under the patient's latest message while a person owes the
   *  reply (e.g. "Sent to Brenda · they'll reply here"). Hidden while typing. */
  pendingReply?: { afterId: string; text: string } | null;
}

export function MessageList({
  messages,
  loading,
  casePlaintext = false,
  nativeAttribution = false,
  requestTitles,
  onOpenRequest,
  renderEvidence,
  renderMessage,
  pendingReply,
  ready = true,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);
  const shownNote = pendingReply && !loading ? pendingReply : null;
  // The note can appear without a new message (a teammate takes the chat), so
  // it follows the same stick-to-bottom rule as messages.
  const noteKey = shownNote ? `${shownNote.afterId}:${shownNote.text}` : null;

  // First scroll: jump to the newest message before paint, once the thread has loaded. The chat
  // can open before the thread (or with only request cards); spending this early left the
  // patient above the newest messages when the thread arrived.
  useLayoutEffect(() => {
    if (!isInitialMount.current || !ready || messages.length === 0) return;
    isInitialMount.current = false;
    bottomRef.current?.scrollIntoView({ behavior: "instant" });
  }, [messages, ready]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || isInitialMount.current) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, noteKey]);

  const items = buildItems(messages);

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto py-15 flex flex-col gap-10"
    >
      {items.map((item, idx) =>
        item.kind === "break" ? (
          <SessionBreak key={`break-${idx}`} date={item.date} />
        ) : (
          <Fragment key={item.message.id}>
            {renderMessage?.(item.message) ?? (
              <MessageBubble
                message={item.message}
                casePlaintext={casePlaintext}
                nativeAttribution={nativeAttribution}
                requestTitle={
                  item.message.ticket_id
                    ? requestTitles?.get(item.message.ticket_id)
                    : undefined
                }
                onOpenRequest={onOpenRequest}
              />
            )}
            {renderEvidence?.(item.message)}
            {shownNote?.afterId === item.message.id && (
              <p className="px-15 text-right text-11 text-ink-muted" role="status">
                {shownNote.text}
              </p>
            )}
          </Fragment>
        ),
      )}
      {loading && (
        <div className="flex items-start gap-10 px-15">
          <div className="bg-sunken border border-line-subtle px-12 py-10">
            <TypingDots />
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}

function SessionBreak({ date }: { date: Date }) {
  return (
    <div className="flex items-center gap-10 px-15 py-5">
      <div className="flex-1 h-px bg-line-subtle" />
      <span className="text-11 text-ink-muted tracking-wide uppercase shrink-0">
        {formatBreakDate(date)}
      </span>
      <div className="flex-1 h-px bg-line-subtle" />
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex gap-4 items-center h-16">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-6 h-6 bg-ink-muted rounded-full animate-pulse"
          style={{ animationDelay: `${i * 150}ms` }}
        />
      ))}
    </div>
  );
}
